<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import Loading from "@/components/Loading.vue";
import CaseFlex from "@/components/CaseFlex.vue";
import CaseFixed from "@/components/CaseFixed.vue";
import ScratchPad from "@/components/ScratchPad/ScratchPad.vue";
import SelectionMenuHost from "@/components/SelectionMenu/SelectionMenuHost.vue";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useMessageStore } from "@/stores/message-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { isItemBurned, isItemBurning } from "@/services/UtilitiesService";

import { useRootLayout } from "@/hooks/use-root-layout";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

withDefaults(defineProps<{ readyToLoad: boolean }>(), {  })

const $route = useRoute();
const $router = useRouter();
const { layout } = useRootLayout();
const routeParams = useCaseRouteParams();

const caseKey = computed(() => `${routeParams.value.client}/${routeParams.value.brand}/${routeParams.value.casename}`);

const loadingState = ref<"success" | "error" | "loading">();

const isCaseRoute = computed(() => {
  if ($route.name == null) return false;
  return $route.name.toString().startsWith("case");
});

const brandStore = useBrandStore();
const isScratchPadEnabled = computed(() => brandStore.featuresConfig.scratchPad !== "disabled");

if (isCaseRoute.value) {
  watch(
    () => __props.readyToLoad,
    async (to) => {
      loadingState.value = "loading";

      if (!to) return;

      try {
        await useCaseStore().onCaseNameChange();

        loadingState.value = "success";
      } catch (r) {
        loadingState.value = "error";
        console.error("Redirecting. Error occurred while entering case", r);

        const statusCode = (r as { status: number }).status;

        $router.push({
          name: "error",
          params: { brand: routeParams.value.brand, code: statusCode },
          replace: statusCode === 403,
        });
      }
    },
    { immediate: true },
  );

  watch(
    [() => routeParams.value.casefile],
    async () => {
      if (routeParams.value.casefile) {
        const { brand, casename, casefile } = routeParams.value;

        useCaseFileStore().addIntoCache({
          fileReference: casefile,
          path: `/${brand}/${casename}/${casefile}`,
        });
      }

      await useCaseFileStore().onSelectedFileChange();
    },
    { immediate: true },
  );

  watch(
    () => useMetadataStore().filteredFiles,
    (filteredFiles) => {
      const { brand, casename, casefile } = routeParams.value;
      if (filteredFiles.length !== 1 || casefile) return;

      $router.replace({
        name: "casefile",
        params: { brand, casename, casefile: filteredFiles[0].fileReference },
      });
    },
    { immediate: true },
  );
}

watch(
  () => useCaseFileStore().currentFile,
  (next, prev) => {
    if (prev == null || next == null) return;

    const isFileBurning = isItemBurning(next, false);

    // If the file is still burning
    // and the flag to optimistically share the update is still true
    if (isFileBurning && useCaseFileStore().cachedFilesData[next.fileReference]?.hasCommittedRedactions === true) {
      // Toggle to false. i.e. the file now has the `edxBurning` flag
      useCaseFileStore().setCommittedRedactionsState(next.fileReference, false);
    }

    // The file wasn't burning or is still burning if
    // - The updated  file         has  edxBurning
    // - The updated  file doesn't have edxBurned
    // - The previous file doesn't have edxBurning
    if (isFileBurning || !isItemBurned(next) || !isItemBurning(prev, false)) return;

    // We should re-select its metadata
    useCaseFileStore().onSelectedFileChange();
  },
);

onUnmounted(() => {
  useMessageStore().clearMessage();
  // Ensure we clear down the metadata in case of a redirect
  useCaseStore().onCaseNameChange();
});
</script>
<template>
  <Loading v-if="loadingState === 'loading' || !readyToLoad" id="case-loading" message="Loading" />
  <div v-else-if="loadingState === 'error'" class="tw-grid tw-size-full tw-place-items-center">
    <div class="tw-max-w-sm tw-text-pretty">Error occurred while entering case</div>
  </div>
  <template v-else>
    <ScratchPad v-if="isCaseRoute && isScratchPadEnabled" :key="caseKey" />
    <SelectionMenuHost v-if="isCaseRoute" :key="caseKey" />
    <CaseFixed v-if="layout === 'FIXED'" :key="caseKey" :is-case-route="isCaseRoute" />
    <CaseFlex v-else-if="layout === 'FLEX'" :key="caseKey" :is-case-route="isCaseRoute" />
  </template>
</template>
